/**
 * Umsatz und offene Forderungen aus den eigenen Rechnungen eines Kontos (Agentur- oder Creator-Konto), EINE Regel für
 * Übersicht, Einstellungen · Firma und Kleinunternehmergrenze (Praxistest K-254). Rein und ohne Datenbank.
 *
 * Umsatz eines Kalenderjahres = Nettobeträge der festgeschriebenen Rechnungen (freigegeben, versendet, bezahlt) nach
 * Rechnungsdatum. Es zählen nicht:
 * - Entwürfe, zurückgezogene, verworfene und abgelehnte Rechnungen;
 * - stornierte Rechnungen und ihre Stornorechnungen (credit_for): beide fallen heraus, auch wenn die Stornorechnung erst im
 *   Folgejahr ausgestellt wurde, das Paar hebt sich auf;
 * - Belege über Verzugskosten (costs_for: Verzugspauschale, Mahnspesen, Zinsen): das ist Schadensersatz, kein Umsatz.
 *
 * Für die Kleinunternehmergrenze zählt zusätzlich nur der Umsatz mit Leistungsort im Inland: Reverse Charge in der EU und
 * nicht im Inland steuerbare Leistungen (Drittland) gehören nicht zum Gesamtumsatz nach § 19 UStG bzw. § 6 Abs. 1 Z 27 UStG.
 * Österreich misst brutto, Deutschland netto; Schweiz und Liechtenstein zählen den Umsatz im In- und Ausland (Art. 10 MWSTG).
 */
import type { Cents } from "./types.js";
import { taxModeFromDb } from "./tax.js";
import type { SmallBusinessRule } from "./small-business-limit.js";

export interface OwnInvoiceRow {
  status: string;
  issue_date: string | null;
  net_cents: number | null;
  gross_cents: number | null;
  currency: string;
  /** Stornorechnung zu dieser Rechnung (0043) */
  credit_for?: string | null;
  /** Beleg über Verzugskosten zu dieser Rechnung (0198) */
  costs_for?: string | null;
  cancelled_at?: string | null;
  tax_mode?: string | null;
  paid_cents?: number | null;
  paid_at?: string | null;
  /** beim Versand eingefrorener Kurs in die Hauswährung des Kontos (0019/0203) */
  fx_rate_to_org?: number | string | null;
}

/** Festgeschrieben: mit Nummer und Rechnungsdatum, nicht mehr änderbar */
const ISSUED = new Set(["approved", "sent", "paid"]);

/** Storniert (die Originalrechnung) */
export function isCancelledInvoice(r: Pick<OwnInvoiceRow, "status" | "cancelled_at">): boolean {
  return r.status === "cancelled" || r.cancelled_at != null;
}

/** Zählt die Rechnung als Umsatz? Festgeschrieben, nicht storniert, keine Stornorechnung, kein Verzugskosten-Beleg. */
export function countsAsRevenue(r: OwnInvoiceRow): boolean {
  return ISSUED.has(r.status) && !isCancelledInvoice(r) && !r.credit_for && !r.costs_for;
}

/** Leistungsort im Ausland (Reverse Charge EU, Drittland): zählt nicht zur inländischen Kleinunternehmergrenze */
export function placeOfSupplyAbroad(r: Pick<OwnInvoiceRow, "tax_mode">): boolean {
  const m = taxModeFromDb(r.tax_mode ?? null);
  return m === "REVERSE_CHARGE_EU" || m === "NOT_TAXABLE_ABROAD";
}

export interface OwnRevenue {
  year: number;
  currency: string;
  /** Umsatz netto (alle Leistungsorte) */
  netCents: Cents;
  /** Umsatz brutto (alle Leistungsorte) */
  grossCents: Cents;
  /** davon Leistungsort Inland, netto bzw. brutto */
  domesticNetCents: Cents;
  domesticGrossCents: Cents;
  /** Anzahl gezählter Rechnungen */
  count: number;
  /** Rechnungen des Jahres in anderen Währungen (zählen hier nicht) */
  otherCurrencies: string[];
}

/** Umsatz eines Kalenderjahres (Rechnungsdatum) in einer Währung aus den eigenen Rechnungen */
export function ownInvoiceRevenue(rows: readonly OwnInvoiceRow[], year: number, currency: string): OwnRevenue {
  const y = String(year);
  const out: OwnRevenue = { year, currency, netCents: 0, grossCents: 0, domesticNetCents: 0, domesticGrossCents: 0, count: 0, otherCurrencies: [] };
  const other = new Set<string>();
  for (const r of rows) {
    if ((r.issue_date ?? "").slice(0, 4) !== y || !countsAsRevenue(r)) continue;
    if (r.currency !== currency) { other.add(r.currency); continue; }
    const net = Number(r.net_cents ?? 0);
    const gross = Number(r.gross_cents ?? r.net_cents ?? 0);
    out.netCents += net;
    out.grossCents += gross;
    out.count += 1;
    if (!placeOfSupplyAbroad(r)) {
      out.domesticNetCents += net;
      out.domesticGrossCents += gross;
    }
  }
  out.otherCurrencies = [...other].sort();
  return out;
}

/**
 * K-279/K-294 (Schweiz-Prüfung 14.10.2026): Umsatz eines Jahres in der Hauswährung einschliesslich der Rechnungen in anderen
 * Währungen, umgerechnet mit dem beim Versand eingefrorenen Kurs (fx_rate_to_org). Für die MWST-Pflicht in CH/LI zählt der
 * weltweite Umsatz, auch in EUR. Rechnungen ohne Kurs (etwa freigegeben, noch nicht versendet) zählt `unconverted`.
 */
export function ownInvoiceRevenueAll(rows: readonly OwnInvoiceRow[], year: number, currency: string): OwnRevenue & { unconverted: number } {
  const y = String(year);
  let unconverted = 0;
  const scaled: OwnInvoiceRow[] = [];
  for (const r of rows) {
    if (r.currency === currency) { scaled.push(r); continue; }
    if ((r.issue_date ?? "").slice(0, 4) !== y || !countsAsRevenue(r)) continue;
    const rate = Number(r.fx_rate_to_org ?? NaN);
    if (!(rate > 0)) { unconverted += 1; continue; }
    scaled.push({ ...r, currency, net_cents: Math.round(Number(r.net_cents ?? 0) * rate), gross_cents: Math.round(Number(r.gross_cents ?? r.net_cents ?? 0) * rate) });
  }
  return { ...ownInvoiceRevenue(scaled, year, currency), unconverted };
}

/** Brutto (Österreich) oder netto (Deutschland, Schweiz, Liechtenstein) misst die Grenze */
export function smallBusinessBasis(rule: Pick<SmallBusinessRule, "country">): "gross" | "net" {
  return rule.country === "AT" ? "gross" : "net";
}

/**
 * Umsatz, der für die Grenze zählt: DE netto, AT brutto, jeweils nur Leistungsort Inland; CH und LI netto im In- und Ausland
 * (die MWST-Pflicht knüpft an den weltweiten Umsatz an, Art. 10 Abs. 2 MWSTG).
 */
export function smallBusinessTurnover(rule: Pick<SmallBusinessRule, "country">, rev: OwnRevenue): Cents {
  if (rule.country === "CH" || rule.country === "LI") return rev.netCents;
  return smallBusinessBasis(rule) === "gross" ? rev.domesticGrossCents : rev.domesticNetCents;
}

/**
 * Offener Rest einer eigenen Rechnung (brutto abzüglich Zahlungen), 0 wenn nichts offen ist. Offen ist nur eine versendete,
 * nicht bezahlte, nicht stornierte Rechnung mit positivem Betrag; Stornorechnungen und Gutschriften mit negativem Betrag nie.
 */
export function ownInvoiceOpenCents(r: OwnInvoiceRow): Cents {
  if (r.status !== "sent" || r.paid_at || isCancelledInvoice(r) || r.credit_for) return 0;
  const gross = Number(r.gross_cents ?? 0);
  if (gross <= 0) return 0;
  return Math.max(0, gross - Math.max(0, Number(r.paid_cents ?? 0)));
}
